import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import IconHighlight from '../../../../IconHighlight';
import WidgetStatePlaceholder from '../../../components/WidgetStatePlaceholder';
import { mapKpiData } from './utils';
import { KPI_STATE_COPY, TREND_CONFIG, TREND_PROGRESS, TREND_TEXT } from './constants';
import WidgetPeriodChip from '../../../catalogWidgets/shared/WidgetPeriodChip';
import { RefreshCw, Info } from 'lucide-react';

// ---------------------------------------------------------------------------
// KpiError — compact horizontal error row matching the KPI Figma spec.
// WidgetStatePlaceholder uses a centered stacked layout that doesn't fit
// the compact height of this widget card.
// ---------------------------------------------------------------------------
const KpiError = ({ errorCode, onRetry, canRetry }) => {
  const isConnection =
    !errorCode ||
    errorCode.toUpperCase().includes('NETWORK') ||
    errorCode.toUpperCase().includes('CONNECTION');
  const copy = isConnection ? KPI_STATE_COPY.CONNECTION_ERROR : KPI_STATE_COPY.SYSTEM_ERROR;
  const Icon = isConnection ? RefreshCw : Info;
  const iconClass = isConnection
    ? 'text-blue-500 dark:text-blue-400'
    : 'text-red-600 dark:text-red-400';

  return (
    <div className="h-full border border-edge rounded-2xl px-4 py-2 flex flex-col items-center justify-center">
      <div className="flex items-center gap-4 w-full">
        <Icon size={32} strokeWidth={1.75} className={`flex-shrink-0 ${iconClass}`} aria-hidden />
        <div className="flex flex-col flex-1 min-w-0 gap-0.5">
          <span className="text-sm font-semibold text-content leading-none">{copy.title}</span>
          <span className="text-xs text-content-muted leading-snug">{copy.message}</span>
        </div>
        {canRetry && onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="flex-shrink-0 h-10 rounded-full border border-edge-strong px-4 py-2 text-sm font-medium text-content whitespace-nowrap hover:bg-surface-hover transition-colors cursor-pointer"
          >
            {copy.buttonText}
          </button>
        )}
      </div>
    </div>
  );
};

KpiError.propTypes = {
  errorCode: PropTypes.string,
  onRetry: PropTypes.func,
  canRetry: PropTypes.bool,
};

// ---------------------------------------------------------------------------
// KpiWidget
// ---------------------------------------------------------------------------
const KpiWidgetInner = ({
  data = null,
  error = null,
  errorCode = '',
  onRetry = undefined,
  canRetry = true,
  isCompressed: _isCompressed,
  icon = null,
  positiveDirection = 'UP',
}) => {
  const mapped = useMemo(
    () => mapKpiData(data, { positiveDirection }),
    [data, positiveDirection],
  );

  if (error) {
    return <KpiError errorCode={errorCode} onRetry={onRetry} canRetry={canRetry} />;
  }

  if (!mapped.value) {
    return <WidgetStatePlaceholder isEmpty stateCopy={KPI_STATE_COPY} />;
  }

  const trendConfig = mapped.trend ? TREND_CONFIG[mapped.trend] : null;
  const resolvedIcon = trendConfig ?? icon ?? null;
  // The threshold the backend evaluated wins over the trend: a rise that is
  // still under target is not a success, and `color_token` is the answer to
  // the rule the author actually wrote.
  const progressColors = TREND_PROGRESS[mapped.tone] ?? TREND_PROGRESS[mapped.trend] ?? TREND_PROGRESS.default;
  const progressStyle = mapped.progress != null ? { width: `${mapped.progress}%` } : null;

  return (
    <div className="flex flex-col gap-3 w-full">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex items-baseline gap-2 min-w-0 flex-wrap">
            <span className="text-xl font-semibold text-content leading-none">{mapped.value}</span>
            {mapped.label && (
              <span className="text-xs font-medium text-content-secondary leading-none">{mapped.label}</span>
            )}
          </div>
          <WidgetPeriodChip period={mapped.period} />
        </div>
        {resolvedIcon && (
          <IconHighlight
            icon={resolvedIcon.icon}
            bgColor={resolvedIcon.bgColor}
            iconColor={resolvedIcon.iconColor}
          />
        )}
      </div>

      {progressStyle && (
        <div className="flex flex-col gap-1">
          <div className={`w-full h-1 rounded-full overflow-hidden ${progressColors.track}`}>
            <div
              className={`h-full rounded-full transition-[width] duration-300 ${progressColors.fill}`}
              style={progressStyle}
            />
          </div>
          {mapped.target && (
            <div className="flex items-baseline justify-between gap-2 text-[11px] text-content-muted">
              {/* «Budget $50,000» / «Target $150,000» — reaching one is the
                  point, reaching the other is the problem. */}
              <span className="truncate">
                {`${mapped.target.label} ${mapped.target.value}`}
              </span>
              {mapped.target.pct != null && (
                <span className="shrink-0">
                  {`${Math.round(mapped.target.pct)}% ${mapped.target.pctLabel}`}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {mapped.feedbackText && (
        <span className="text-xs font-medium text-content-muted">{mapped.feedbackText}</span>
      )}

      {/* One row per comparison, each showing the number `compare_as` asked
          for. Up to three — the contract caps the list there. */}
      {mapped.comparisons.length > 0 && (
        <div className="flex flex-col gap-1 border-t border-edge pt-2">
          {mapped.comparisons.map((comparison) => (
            <div key={comparison.key} className="flex flex-wrap items-baseline gap-x-1 text-xs">
              <span className={`font-semibold ${TREND_TEXT[comparison.trend] ?? 'text-content'}`}>
                {comparison.figure}
              </span>
              <span className="text-content-secondary">
                {`${comparison.preposition} ${comparison.label}`}
              </span>
              {comparison.context ? (
                <span className="text-content-muted">{`(${comparison.context})`}</span>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

KpiWidgetInner.propTypes = {
  /**
   * The type's `data`, exactly as `KPI/output.schema.json` publishes it.
   * `mapKpiData` turns it into the render model — see its own note.
   */
  data: PropTypes.shape({
    value: PropTypes.number,
    label: PropTypes.string,
    format: PropTypes.object,
    caption: PropTypes.string,
    comparison_value: PropTypes.number,
    delta: PropTypes.number,
    delta_pct: PropTypes.number,
    trend: PropTypes.oneOf(['UP', 'DOWN', 'FLAT', null]),
    target_value: PropTypes.number,
    target_pct: PropTypes.number,
    // COST_KPI's own names for the same bar — see `mapKpiData`.
    budget_value: PropTypes.number,
    budget_used_pct: PropTypes.number,
    currency_code: PropTypes.string,
    color_token: PropTypes.string,
    period: PropTypes.object,
    comparisons: PropTypes.arrayOf(PropTypes.object),
  }),
  error: PropTypes.shape({ message: PropTypes.string, code: PropTypes.string }),
  errorCode: PropTypes.string,
  onRetry: PropTypes.func,
  canRetry: PropTypes.bool,
  isCompressed: PropTypes.bool,
  /**
   * The author's `display.positive_direction`. `DOWN` for the measures where
   * a rise is the bad news — cost, waste, time to resolve.
   */
  positiveDirection: PropTypes.oneOf(['UP', 'DOWN']),
  icon: PropTypes.shape({
    icon: PropTypes.elementType.isRequired,
    bgColor: PropTypes.string,
    iconColor: PropTypes.string,
  }),
};

const KpiWidget = React.memo(KpiWidgetInner);

export default KpiWidget;
